/**
 * pagination.js — 全站分頁共用核心（純函式 + 統一 item 樣板）
 *
 * 單一真相源：後台 admin-table.js `_renderPager` 與前台 bind-client-pagination.js 都吃
 * buildPaginationItems()，確保省略號規則 / 邊界行為 / a11y 屬性逐字一致，視覺沿用
 * pagination.css 既有 atom（不重做）。
 *
 * 省略號規則（對齊 uiux 規格）：
 *   - 首頁 1 + 末頁 total 恆顯；當前頁 ±siblings 顯；缺口塞單一 '…'。
 *   - total <= 2*boundaries + 2*siblings + 3 全列不省略（預設 opts → 7）；超過才省略。
 *   - '…' 只代表 >=2 個被省頁；缺口剛好夾 1 頁時展開該頁（不放 '…'）。
 *   - prev disabled =(current===1)；next disabled =(current===total)。
 *
 * 邊界範例（total=20, siblings=1, boundaries=1）：
 *   cur3  → 1 2 3 4 5 … 20
 *   cur10 → 1 … 9 10 11 … 20
 *   cur18 → 1 … 16 17 18 19 20
 *
 * 演算法本體與 rc-pagination / MUI usePagination 同源（boundary + sibling window +
 * 「少一個省略號時補一格」校正），經 total=1..60 全掃不變式驗證（… 永不只夾 1 頁、
 * 數字嚴格遞增、恆含 1 與 total）。
 *
 * 載入：純函式 + 樣板，不 auto-init。後台 admin-table.js（_renderPager）、前台
 * bind-client-pagination.js 與 pages/search.js import 使用（跨 main.js / admin.js 兩鏈）。
 */

/** 連續整數陣列 [start..end]（end < start 回空陣列）。 */
function range(start, end) {
  if (end < start) return [];
  const out = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}

/**
 * 計算分頁項目序列。
 *
 * @param {number} current 目前頁（1-based）
 * @param {number} total   總頁數
 * @param {{siblings?:number, boundaries?:number}} [opts]
 *   siblings：當前頁左右各顯示幾頁（預設 1）；boundaries：首尾各固定顯示幾頁（預設 1）。
 * @returns {Array<{type:'prev'|'page'|'ellipsis'|'next', page?:number, active?:boolean, disabled?:boolean}>}
 *   prev/next 端點以 disabled 標記（不省略，永遠在序列首尾）；page 含 active；ellipsis 無 page。
 */
export function buildPaginationItems(current, total, opts = {}) {
  const siblings = opts.siblings ?? 1;
  const boundaries = opts.boundaries ?? 1;

  // total 與 current 防呆：至少 1 頁，current 夾在 [1, total]。
  const totalPages = Math.max(1, Math.floor(total) || 1);
  const cur = Math.min(Math.max(1, Math.floor(current) || 1), totalPages);

  const pages = buildPageNumbers(cur, totalPages, siblings, boundaries);

  const items = [];
  items.push({ type: 'prev', page: cur - 1, disabled: cur === 1 });
  pages.forEach((p) => {
    if (p === 'ellipsis') items.push({ type: 'ellipsis' });
    else items.push({ type: 'page', page: p, active: p === cur });
  });
  items.push({ type: 'next', page: cur + 1, disabled: cur === totalPages });
  return items;
}

/**
 * 產生頁碼序列（數字與 'ellipsis' 交錯），不含 prev/next。
 * 回傳如 [1,'ellipsis',9,10,11,'ellipsis',20]。
 */
function buildPageNumbers(cur, totalPages, siblings, boundaries) {
  // 全列門檻：首尾 boundaries + 當前 + 兩側 siblings + 兩個省略號佔位。
  // 預設 → 2 + 1 + 2 + 2 = 7：total<=7 全列不省略。
  const totalPageNumbers = boundaries * 2 + siblings * 2 + 3;
  if (totalPages <= totalPageNumbers) return range(1, totalPages);

  const leftSiblingIndex = Math.max(cur - siblings, boundaries + 1);
  const rightSiblingIndex = Math.min(cur + siblings, totalPages - boundaries);

  // 省略號只在「被省頁 >=2」時出現（缺口剛好 1 頁 → 展開那頁，不放 '…'）。
  const showLeftEllipsis = leftSiblingIndex > boundaries + 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - boundaries - 1;

  const firstPages = range(1, boundaries);
  const lastPages = range(totalPages - boundaries + 1, totalPages);

  // 只省右側：左塊補一格（少一個省略號），維持序列長度穩定。
  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = boundaries + siblings * 2 + 2;
    return [...range(1, leftItemCount), 'ellipsis', ...lastPages];
  }
  // 只省左側：右塊補一格。
  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = boundaries + siblings * 2 + 2;
    return [...firstPages, 'ellipsis', ...range(totalPages - rightItemCount + 1, totalPages)];
  }
  // 兩側皆省略：固定首尾 + 中段 window。
  return [
    ...firstPages,
    'ellipsis',
    ...range(leftSiblingIndex, rightSiblingIndex),
    'ellipsis',
    ...lastPages,
  ];
}

/* ===== 統一 item 樣板（兩機制 class 逐字相同，只差 tag）===== */

const PREV_GLYPH = '‹';
const NEXT_GLYPH = '›';
const ELLIPSIS_GLYPH = '…';

/** prev/next 的 aria-label（共用，兩機制一致）。 */
const ARIA_LABEL = { prev: '上一頁', next: '下一頁' };

/**
 * 渲染單一項目為 HTML 字串（URL 連結式，前台用）。
 * page → <a href="?page=N">；prev/next 同 + --prev/--next + aria-label；端點 aria-disabled。
 * ellipsis → <span aria-hidden>。
 *
 * @param {object} item buildPaginationItems 的單一項
 * @param {(page:number)=>string} hrefFor 由頁碼產生 href（呼叫端決定 query 形式）
 */
export function renderItemLink(item, hrefFor) {
  if (item.type === 'ellipsis') {
    return `<span class="pagination__ellipsis" aria-hidden="true">${ELLIPSIS_GLYPH}</span>`;
  }
  // data-page 供 JS client 分頁直接讀目標頁（免解析 href）；href 仍保留作無 JS / 中鍵開新分頁降級。
  if (item.type === 'prev' || item.type === 'next') {
    const glyph = item.type === 'prev' ? PREV_GLYPH : NEXT_GLYPH;
    const cls = `pagination__item pagination__item--${item.type}${item.disabled ? ' is-disabled' : ''}`;
    const dis = item.disabled ? ' aria-disabled="true"' : '';
    return `<a class="${cls}" href="${hrefFor(item.page)}" data-page="${item.page}" aria-label="${ARIA_LABEL[item.type]}"${dis}>${glyph}</a>`;
  }
  const cls = `pagination__item${item.active ? ' is-active' : ''}`;
  const cur = item.active ? ' aria-current="page"' : '';
  return `<a class="${cls}" href="${hrefFor(item.page)}" data-page="${item.page}"${cur}>${item.page}</a>`;
}

/**
 * 渲染單一項目為 HTML 字串（button 式，後台 JS renderer 用）。
 * page → <button data-page=N>；prev/next 同 + --prev/--next + aria-label；端點 aria-disabled。
 * ellipsis → <span aria-hidden>（與連結式相同）。
 */
export function renderItemButton(item) {
  if (item.type === 'ellipsis') {
    return `<span class="pagination__ellipsis" aria-hidden="true">${ELLIPSIS_GLYPH}</span>`;
  }
  if (item.type === 'prev' || item.type === 'next') {
    const glyph = item.type === 'prev' ? PREV_GLYPH : NEXT_GLYPH;
    const cls = `pagination__item pagination__item--${item.type}`;
    const dis = item.disabled ? ' aria-disabled="true"' : '';
    return `<button class="${cls}" type="button" aria-label="${ARIA_LABEL[item.type]}"${dis} data-page="${item.page}">${glyph}</button>`;
  }
  const cls = `pagination__item${item.active ? ' is-active' : ''}`;
  const cur = item.active ? ' aria-current="page"' : '';
  return `<button class="${cls}" type="button"${cur} data-page="${item.page}">${item.page}</button>`;
}

/**
 * compact 變體（≤640px 行動裝置 / 側欄）：‹ + 「cur / total」+ ›。
 * 與 atom .pagination--compact .pagination__current 對齊。呼叫端負責在容器加 --compact class。
 *
 * @param {number} current
 * @param {number} total
 * @param {{mode?:'link'|'button', hrefFor?:(page:number)=>string}} [opts]
 *   mode='link'（預設）產 <a href>；'button' 產 <button data-page>。link 模式須給 hrefFor。
 */
export function renderCompact(current, total, opts = {}) {
  const totalPages = Math.max(1, Math.floor(total) || 1);
  const cur = Math.min(Math.max(1, Math.floor(current) || 1), totalPages);
  const prevDisabled = cur === 1;
  const nextDisabled = cur === totalPages;
  const mode = opts.mode === 'button' ? 'button' : 'link';

  const endpoint = (type, page, disabled) => {
    const glyph = type === 'prev' ? PREV_GLYPH : NEXT_GLYPH;
    const cls = `pagination__item pagination__item--${type}${disabled && mode === 'link' ? ' is-disabled' : ''}`;
    const dis = disabled ? ' aria-disabled="true"' : '';
    if (mode === 'button') {
      return `<button class="${cls}" type="button" aria-label="${ARIA_LABEL[type]}"${dis} data-page="${page}">${glyph}</button>`;
    }
    const href = opts.hrefFor ? opts.hrefFor(page) : '#';
    return `<a class="${cls}" href="${href}" data-page="${page}" aria-label="${ARIA_LABEL[type]}"${dis}>${glyph}</a>`;
  };

  return [
    endpoint('prev', cur - 1, prevDisabled),
    `<span class="pagination__current">${cur} / ${totalPages}</span>`,
    endpoint('next', cur + 1, nextDisabled),
  ].join('');
}
